import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2, ShieldCheck, Globe2, Zap, DollarSign, FileCheck2, HandshakeIcon } from "lucide-react";
import Seo, { ORG_LD } from "@/components/site/Seo";
import { RevealLines } from "@/components/site/RevealText";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  company: "", contact_name: "", email: "", phone: "", country: "",
  website: "", capabilities: "", capacity: "", certifications: "",
  years_operating: "", message: "",
};

const BENEFITS = [
  { icon: Globe2, title: "Direct access to Western buyers", body: "RFQs from procurement teams in the USA, Canada, EU, UAE and GCC — reaching your desk directly, without the middle layers or trading-house games." },
  { icon: FileCheck2, title: "Pre-qualified, engineer-normalised briefs", body: "Every requirement is reviewed by a NEXORIQ engineer before it reaches you — specifications, drawings, standards and quantities in a build-ready packet." },
  { icon: DollarSign, title: "Clear scope, fair payment", body: "One PO. One accountable operator. Scope, price and payment terms locked before you commit shop-floor capacity — no chasing, no last-minute changes." },
  { icon: HandshakeIcon, title: "Long-term repeat work", body: "The goal is not one-off orders. We are looking to place recurring, framework and repeat volumes with a smaller number of high-trust factories." },
  { icon: ShieldCheck, title: "Compliance & documentation support", body: "MTC (EN 10204 3.1 / 3.2), PPAP, PMI, third-party inspection coordination and export documentation — we own the paperwork end so you can own the shop-floor end." },
  { icon: Zap, title: "Faster feedback loops", body: "One point of contact at NEXORIQ per program. Quick clarifications, decisive answers, no email trees." },
];

export default function Partners() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact_name || !form.email || !form.country || !form.capabilities || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/partnership`, form);
      setSuccess(true);
      setForm(initial);
      toast.success("Application received.");
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Something went wrong.";
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  const input =
    "w-full bg-transparent border-b border-nx-navy/50 focus:border-nx-copper py-3 outline-none font-ibm text-nx-navy placeholder:text-nx-steel2 transition-colors";

  return (
    <div className="bg-nx-bg text-nx-ink" data-testid="page-partners">
      <Seo
        title="Join Our Manufacturer Network · Partner With NEXORIQ"
        description="Manufacturer or supplier? Apply to join the NEXORIQ partner network and get direct access to sourcing briefs from Western buyers — engineer-normalised, pre-qualified, and framework-ready."
        path="/partners"
        structuredData={ORG_LD}
      />

      {/* Hero */}
      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 pt-32 md:pt-40 pb-24" data-testid="partners-hero">
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-nx-copper mb-6">Partner Network · Manufacturer Application</div>
        <RevealLines
          as="h1"
          lines={[<>Manufacture</>, <>with <span className="text-nx-copper">NEXORIQ.</span></>]}
          className="font-cabinet font-black tracking-[-0.04em] leading-[0.92] text-[13vw] md:text-[7vw] lg:text-[5.4vw] text-nx-bg"
        />
        <p className="mt-8 max-w-2xl text-nx-bg/80 leading-relaxed text-lg">
          If your factory holds the codes, the capacity and the discipline — we'll bring you briefs
          from Western buyers already qualified to your capability sheet. No cold intros. No
          trading-house layers. Engineer-to-engineer, one accountable partner.
        </p>
      </section>

      {/* Benefits — 6 chips */}
      <section className="px-6 md:px-12 lg:px-20 py-24" data-testid="partner-benefits">
        <div className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-nx-copper mb-6">Why partner with NEXORIQ</div>
        <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[5vw] lg:text-[4.4vw] text-nx-navy max-w-4xl">
          Network benefits with the Western world.
        </h2>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-nx-navy/20 border border-nx-navy/25">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ delay: i * 0.05, duration: 0.55 }}
                className="bg-nx-bg p-8 md:p-10"
                data-testid={`partner-benefit-${i}`}
              >
                <div className="flex items-center justify-between mb-8">
                  <Icon size={22} className="text-nx-copper" />
                  <span className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-nx-copper">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="font-cabinet font-black text-xl tracking-tight text-nx-navy leading-tight">{b.title}</h3>
                <p className="mt-3 text-nx-navy2 text-sm leading-relaxed">{b.body}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Application form */}
      <section className="bg-nx-bg2 px-6 md:px-12 lg:px-20 py-24" data-testid="partner-apply">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-14">
          <aside className="lg:col-span-4">
            <div className="font-mono text-[0.65rem] uppercase tracking-[0.28em] text-nx-copper mb-6">Apply · Supplier onboarding</div>
            <h2 className="font-cabinet font-black tracking-[-0.03em] leading-[0.95] text-3xl md:text-4xl text-nx-navy">
              Show us what your shop can do.
            </h2>
            <p className="mt-6 text-nx-navy2 leading-relaxed">
              A team member from the NEXORIQ sourcing desk will review your application within 5
              business days. If there's a fit, we'll schedule a virtual factory tour and a technical
              audit.
            </p>
            <div className="mt-8 pt-6 border-t border-nx-navy/25 text-sm text-nx-navy2 space-y-2">
              <div><span className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-nx-copper mr-2">01</span>Application received</div>
              <div><span className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-nx-copper mr-2">02</span>Capability & document review</div>
              <div><span className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-nx-copper mr-2">03</span>Virtual factory tour</div>
              <div><span className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-nx-copper mr-2">04</span>Onboarded to briefs</div>
            </div>
          </aside>

          <div className="lg:col-span-8 border border-nx-navy bg-white p-6 md:p-10" data-testid="partner-form-card">
            {success ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="partner-success">
                <p className="font-mono text-[0.65rem] uppercase tracking-[0.24em] text-nx-copper mb-4">→ Received</p>
                <h3 className="font-cabinet font-black text-3xl md:text-4xl text-nx-navy leading-tight">Application received.</h3>
                <p className="mt-4 text-nx-navy2 leading-relaxed max-w-md">
                  Thanks. Our sourcing desk will review your capabilities and revert within 5 business days.
                </p>
                <button onClick={() => setSuccess(false)} className="nx-btn mt-6" data-testid="partner-new">
                  Submit another <ArrowUpRight size={14} />
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="partner-form" className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <Field label="Company name" req><input required value={form.company} onChange={upd("company")} className={input} data-testid="p-company" /></Field>
                <Field label="Contact name" req><input required value={form.contact_name} onChange={upd("contact_name")} className={input} data-testid="p-contact-name" /></Field>
                <Field label="Email" req><input required type="email" value={form.email} onChange={upd("email")} className={input} data-testid="p-email" /></Field>
                <Field label="Phone"><input value={form.phone} onChange={upd("phone")} className={input} data-testid="p-phone" /></Field>
                <Field label="Country" req><input required value={form.country} onChange={upd("country")} className={input} data-testid="p-country" /></Field>
                <Field label="Website"><input value={form.website} onChange={upd("website")} className={input} placeholder="https://…" data-testid="p-website" /></Field>
                <Field label="Years operating"><input value={form.years_operating} onChange={upd("years_operating")} className={input} placeholder="e.g. 12" data-testid="p-years" /></Field>
                <Field label="Approx. annual capacity"><input value={form.capacity} onChange={upd("capacity")} className={input} placeholder="e.g. 5,000 t forgings / yr" data-testid="p-capacity" /></Field>
                <Field label="Core capabilities" req full><textarea required rows={2} value={form.capabilities} onChange={upd("capabilities")} className={input + " resize-none"} placeholder="CNC, forging, casting, injection moulding, PCBA…" data-testid="p-capabilities" /></Field>
                <Field label="Certifications" full><input value={form.certifications} onChange={upd("certifications")} className={input} placeholder="ISO 9001, API 6A, ASME U, IATF 16949…" data-testid="p-certs" /></Field>
                <Field label="Anything else you want us to know" req full><textarea required rows={4} value={form.message} onChange={upd("message")} className={input + " resize-none"} placeholder="Reference customers, key facility highlights, unique tooling…" data-testid="p-message" /></Field>
                <div className="md:col-span-2 flex justify-end pt-4 border-t border-nx-navy/20">
                  <button type="submit" disabled={loading} className="nx-btn disabled:opacity-60" data-testid="p-submit">
                    {loading ? (<><Loader2 size={14} className="animate-spin" /> Submitting</>) : (<>Submit application <ArrowUpRight size={14} /></>)}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Closing note */}
      <section className="bg-nx-navydark text-nx-bg px-6 md:px-12 lg:px-20 py-20 md:py-24" data-testid="partners-note">
        <h2 className="font-cabinet font-black tracking-[-0.035em] leading-[0.95] text-[10vw] md:text-[4.4vw] text-nx-bg max-w-4xl">
          We only onboard factories we'd stake our own name on.
        </h2>
        <p className="mt-6 text-nx-bg/75 max-w-2xl leading-relaxed">
          If your shop is aligned with our commitments on quality, HSE and labour standards, we'd
          rather bring you fewer briefs, better matched, than flood you with noise. Long-term over
          transactional.
        </p>
      </section>
    </div>
  );
}

function Field({ label, req, full, children }) {
  return (
    <label className={"block " + (full ? "md:col-span-2" : "")}>
      <span className="flex items-center mb-2 font-mono text-[0.6rem] uppercase tracking-[0.2em] text-nx-copper">
        {label}
        {req && <span className="text-nx-copper ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
